import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import CommonMultiSelect from './CommonMultiSelect';

/**
 * Config-driven form modal — a reusable wrapper over react-bootstrap Modal,
 * styled for the app's dark surfaces. The modal owns its form via
 * react-hook-form: pass a `fields` config (with validation rules) and a
 * `buttons` config, and it renders, validates, and reports errors for you.
 *
 * Usage:
 *   <CommonModal
 *       show={open}
 *       onHide={() => setOpen(false)}
 *       title="Add Report Recipient"
 *       subtitle="They'll receive scheduled reports by email."
 *       fields={[
 *           { name: 'email',       label: 'Email',       type: 'email',    placeholder: 'name@company.com', required: true },
 *           { name: 'name',        label: 'Name',        type: 'text',     half: true, required: true },
 *           { name: 'designation', label: 'Designation', type: 'text',     half: true },
 *           { name: 'role',        label: 'Role',        type: 'select',   options: [{ value: 'a', label: 'A' }] },
 *           { name: 'is_active',   label: 'Active',      type: 'checkbox' },
 *       ]}
 *       defaultValues={{ email: '', name: '', designation: '', is_active: true }}
 *       onSubmit={(data) => save(data)}       // receives validated values
 *       error={submitError}                    // server-side error string
 *       buttons={[
 *           { text: 'Cancel',        variant: 'secondary', onClick: () => setOpen(false), disabled: busy },
 *           { text: 'Add Recipient', variant: 'primary',   type: 'submit', loading: busy, loadingText: 'Saving…' },
 *       ]}
 *   />
 *
 * Field config: { name, label, type, placeholder, options, half, disabled, rows,
 *                 required, pattern, minLength, maxLength, validate }
 *   type ∈ text | email | password | number | tel | url | textarea | select | multiselect | checkbox   (default: text)
 *   half: true  → renders at 50% width so two consecutive fields sit side by side
 *   options     → [{ value, label }] for `select` and `multiselect`
 *   `type: 'multiselect'` renders a checkbox dropdown; its form value is an
 *   array of selected `value`s — default it to [] in defaultValues.
 *   Validation (react-hook-form rules): `required` accepts true or a custom message;
 *   `pattern`/`minLength`/`maxLength`/`validate` are passed straight through.
 *   `type: 'email'` auto-applies an email pattern unless one is supplied.
 *   `type: 'password'` renders a show/hide eye toggle.
 *   `validate` receives (value, allValues) — the 2nd arg enables cross-field
 *   rules, e.g. validate: (v, all) => v === all.newPassword || 'Passwords must match'.
 *
 * Button config: { text, onClick, variant, type, loading, loadingText, disabled }
 *   variant ∈ primary | secondary | danger   (default: secondary)
 *   type    ∈ button | submit                 (default: button); a `submit` button
 *             triggers validation before onSubmit runs.
 *
 * Sizing (pick one): boolean shorthands `small` (narrow, sm), `mid` (roomy, lg),
 *   `fullscreen` (fills the viewport). Or pass `size` ∈ 'sm' | 'lg' | 'xl'
 *   directly (explicit `size` wins over the shorthands). Default = medium.
 *   `fullscreen` also accepts a breakpoint ('sm-down' | 'md-down' | 'lg-down' |
 *   'xl-down' | 'xxl-down'). Use fullscreen/mid when the form has room-hungry
 *   controls (e.g. multi-select dropdowns) that shouldn't be cramped.
 */
const EMAIL_PATTERN = { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' };

const EYE_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" />
    </svg>
);
const EYE_OFF_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

function buildRules(field) {
    const rules = {};
    if (field.required) {
        const requiredMsg = typeof field.required === 'string' ? field.required : `${field.label} is required`;
        // A multiselect value is an array — an empty [] is truthy, so the
        // built-in `required` never fires. Enforce non-empty via validate.
        if (field.type === 'multiselect') {
            rules.validate = (v) => (Array.isArray(v) && v.length > 0) || requiredMsg;
        } else {
            rules.required = requiredMsg;
        }
    }
    if (field.pattern) rules.pattern = field.pattern;
    else if (field.type === 'email') rules.pattern = EMAIL_PATTERN;
    if (field.minLength) rules.minLength = field.minLength;
    if (field.maxLength) rules.maxLength = field.maxLength;
    if (field.validate) rules.validate = field.validate;   // caller-supplied validate wins
    return rules;
}

function Field({ field, register, control, error }) {
    const { name, label, type = 'text', placeholder, options = [], required, half, disabled, rows } = field;
    const [reveal, setReveal] = useState(false);
    // multiselect is controlled via RHF Controller; everything else uses register.
    const reg = type === 'multiselect' ? null : register(name, buildRules(field));

    if (type === 'checkbox') {
        return (
            <label className="cmn-modal-checkbox">
                <input type="checkbox" disabled={disabled} {...reg} />
                <span>{label}</span>
            </label>
        );
    }

    let inputEl;
    if (type === 'select') {
        inputEl = (
            <select className="cmn-modal-input" disabled={disabled} {...reg}>
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        );
    } else if (type === 'multiselect') {
        inputEl = (
            <Controller
                name={name}
                control={control}
                rules={buildRules(field)}
                render={({ field: f }) => (
                    <CommonMultiSelect value={f.value} onChange={f.onChange}
                        options={options} placeholder={placeholder} disabled={disabled} />
                )}
            />
        );
    } else if (type === 'textarea') {
        inputEl = <textarea className="cmn-modal-input cmn-modal-textarea" placeholder={placeholder} disabled={disabled} rows={rows || 3} {...reg} />;
    } else if (type === 'password') {
        inputEl = (
            <div className="cmn-modal-input-wrap">
                <input className="cmn-modal-input" type={reveal ? 'text' : 'password'} placeholder={placeholder} disabled={disabled} {...reg} />
                <button type="button" className="cmn-modal-eye" tabIndex={-1}
                    onClick={() => setReveal(r => !r)} aria-label={reveal ? 'Hide password' : 'Show password'}>
                    {reveal ? EYE_OFF_ICON : EYE_ICON}
                </button>
            </div>
        );
    } else {
        inputEl = <input className="cmn-modal-input" type={type} placeholder={placeholder} disabled={disabled} {...reg} />;
    }

    return (
        <div className={`cmn-modal-field${half ? ' cmn-modal-field--half' : ''}`}>
            <label className="cmn-modal-label">{label}{required && <span className="cmn-modal-req">*</span>}</label>
            {inputEl}
            {error && <p className="cmn-modal-field-err">{error.message}</p>}
        </div>
    );
}

function CommonModal({
    show, onHide, title, subtitle,
    fields = [], defaultValues = {}, onSubmit, error, children,
    buttons = [], size, centered = true, fullscreen = false,
    small = false, mid = false,
}) {
    const resolvedSize = size || (small ? 'sm' : mid ? 'lg' : undefined);
    const { register, control, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues });

    // Reset to fresh defaults each time the modal opens.
    useEffect(() => {
        if (show) reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const submit = (data) => { if (onSubmit) onSubmit(data); };

    return (
        <Modal show={show} onHide={onHide} centered={fullscreen ? false : centered} size={resolvedSize} fullscreen={fullscreen || undefined} contentClassName="cmn-modal-content">
            <style>{CMN_MODAL_CSS}</style>
            <form className="cmn-modal-form" onSubmit={handleSubmit(submit)} noValidate>
                <div className="cmn-modal-header">
                    <div>
                        <h3 className="cmn-modal-title">{title}</h3>
                        {subtitle && <p className="cmn-modal-subtitle">{subtitle}</p>}
                    </div>
                    <button type="button" className="cmn-modal-close" onClick={onHide} aria-label="Close">×</button>
                </div>

                <div className="cmn-modal-body">
                    <div className="cmn-modal-fields">
                        {fields.map(f => (
                            <Field key={f.name} field={f} register={register} control={control} error={errors[f.name]} />
                        ))}
                    </div>
                    {children}
                    {error && <p className="cmn-modal-error">{error}</p>}
                </div>

                <div className="cmn-modal-footer">
                    {buttons.map((b, i) => (
                        <button
                            key={i}
                            type={b.type || 'button'}
                            className={`cmn-modal-btn cmn-modal-btn--${b.variant || 'secondary'}`}
                            onClick={b.onClick}
                            disabled={b.disabled || b.loading}
                        >
                            {b.loading ? (b.loadingText || 'Please wait…') : b.text}
                        </button>
                    ))}
                </div>
            </form>
        </Modal>
    );
}

const CMN_MODAL_CSS = `
.cmn-modal-content {
    background: #0f172a !important;
    border: 1px solid #1e293b !important;
    border-radius: 14px !important;
    box-shadow: 0 24px 60px -12px rgba(0,0,0,0.6) !important;
    font-family: 'Outfit', sans-serif;
    overflow: hidden;
}
.cmn-modal-form { display: flex; flex-direction: column; }
.cmn-modal-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 20px 24px 14px; border-bottom: 1px solid #1e293b;
}
.cmn-modal-title { margin: 0; color: #f1f5f9; font-size: 17px; font-weight: 600; }
.cmn-modal-subtitle { margin: 4px 0 0; color: #64748b; font-size: 12.5px; line-height: 1.5; }
.cmn-modal-close {
    background: transparent; border: none; color: #64748b; font-size: 24px; line-height: 1;
    cursor: pointer; padding: 0 4px; margin: -4px -4px 0 12px; transition: color 0.15s ease;
}
.cmn-modal-close:hover { color: #e2e8f0; }

.cmn-modal-body { padding: 20px 24px; max-height: 82vh; overflow-y: auto; overflow-x: auto; }
.cmn-modal-fields { display: flex; flex-wrap: wrap; gap: 16px; }
.cmn-modal-field { flex: 1 1 100%; display: flex; flex-direction: column; }
.cmn-modal-field--half { flex: 1 1 calc(50% - 8px); }
.cmn-modal-label { color: #cbd5e1; font-size: 12px; font-weight: 500; margin-bottom: 6px; }
.cmn-modal-req { color: #ef4444; margin-left: 3px; }
.cmn-modal-input {
    width: 100%; background: #0b1220; border: 1px solid #334155; color: #f1f5f9;
    border-radius: 8px; font-size: 13px; padding: 9px 12px; outline: none;
    font-family: 'Outfit', sans-serif; transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.cmn-modal-input::placeholder { color: #475569; }
.cmn-modal-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.18); }
.cmn-modal-input:disabled { opacity: 0.6; cursor: not-allowed; }
.cmn-modal-textarea { resize: vertical; min-height: 72px; }
select.cmn-modal-input { cursor: pointer; }

.cmn-modal-input-wrap { position: relative; display: flex; align-items: center; }
.cmn-modal-input-wrap .cmn-modal-input { padding-right: 40px; }
.cmn-modal-eye {
    position: absolute; right: 8px; display: flex; align-items: center; justify-content: center;
    background: transparent; border: none; color: #64748b; cursor: pointer; padding: 4px;
    transition: color 0.15s ease;
}
.cmn-modal-eye:hover { color: #cbd5e1; }

.cmn-modal-checkbox {
    flex: 1 1 100%; display: flex; align-items: center; gap: 9px; cursor: pointer;
    color: #cbd5e1; font-size: 13px; font-weight: 500; user-select: none;
}
.cmn-modal-checkbox input { width: 16px; height: 16px; cursor: pointer; accent-color: #3b82f6; }

.cmn-modal-field-err { color: #f87171; font-size: 11.5px; margin: 5px 0 0; }
.cmn-modal-error {
    color: #f87171; font-size: 12.5px; margin: 16px 0 0;
    background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.25);
    border-radius: 8px; padding: 8px 12px;
}

.cmn-modal-footer {
    display: flex; justify-content: flex-end; gap: 10px;
    padding: 14px 24px 20px; border-top: 1px solid #1e293b;
}
.cmn-modal-btn {
    padding: 9px 18px; font-size: 13px; font-weight: 600; border-radius: 8px;
    cursor: pointer; border: 1px solid transparent; font-family: 'Outfit', sans-serif;
    transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}
.cmn-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.cmn-modal-btn--secondary { background: transparent; border-color: #334155; color: #cbd5e1; }
.cmn-modal-btn--secondary:hover:not(:disabled) { background: #1e293b; }
.cmn-modal-btn--primary { background: #3b82f6; color: #fff; }
.cmn-modal-btn--primary:hover:not(:disabled) { background: #2563eb; }
.cmn-modal-btn--danger { background: #dc2626; color: #fff; }
.cmn-modal-btn--danger:hover:not(:disabled) { background: #b91c1c; }

/* Fullscreen mode — let the form fill the viewport and the body take all the
   leftover height (so dropdowns/long content have plenty of room). */
.modal-fullscreen .cmn-modal-content,
[class*="modal-fullscreen"] .cmn-modal-content { height: 100%; border-radius: 0 !important; }
.modal-fullscreen .cmn-modal-form,
[class*="modal-fullscreen"] .cmn-modal-form { height: 100%; }
.modal-fullscreen .cmn-modal-body,
[class*="modal-fullscreen"] .cmn-modal-body { max-height: none; flex: 1 1 auto; }
`;

export default CommonModal;
